<template>
  <div class="pl-3">
    <v-row justify="center" align="center">
      <v-combobox
        label="Load Column Set"
        dense
        :outlined="outlinedInputs"
        hide-details
        :items="setsList"
        item-value="id"
        item-text="name"
        @change="onSetChange"
        v-model="selectedSet"
        v-if="setsList.length > 0"
      >
        <template v-slot:item="{ item }">
          <div>{{ item.name }}</div>
          <v-spacer></v-spacer>
          <v-btn icon @click.stop.prevent="onDeleteSet(item.id)" class="ml-2">
            <v-icon small color="error">mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-combobox>

      <v-tooltip bottom v-if="currentColumns.length > 0">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            color="primary"
            dark
            v-bind="attrs"
            v-on="on"
            text
            icon
            @click="onSaveColumnSet"
            class="ml-1"
          >
            <v-icon>mdi-content-save-outline</v-icon>
          </v-btn>
        </template>
        <span>Save Current Column Set</span>
      </v-tooltip>
    </v-row>
    <basic-dialog
      :show="showDialog"
      @close="showDialog = false"
      max-width="550"
    >
      <template v-slot:title>Save Current Column Set</template>
      <v-row>
        <v-col>
          <v-text-field
            label="Set Name"
            v-model="setName"
            :outlined="outlinedInputs"
            hide-details
            :disabled="!saveEnabled"
          ></v-text-field>
        </v-col>
      </v-row>
      <template v-slot:actions>
        <v-btn color="primary" @click="saveColumnSet" :disabled="!isValid"
          >Save</v-btn
        >
      </template>
    </basic-dialog>
    <v-row v-if="debug">
      <v-col>
        <code>selectedSet: {{ selectedSet }}</code>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import gql from "graphql-tag";
import BasicDialog from "./BasicDialog.vue";
import { removeGQLTypename } from "../../../helpers";

const CREATE_COLUMN_SET = gql`
  mutation createColumnSet($input: CustomColumnSetInput) {
    createCustomColumnSet(input: $input) {
      id
      name
      columns
    }
  }
`;

const SETS_LIST = gql`
  query userCustomColumnSets($refName: String) {
    customColumnSets(filter: { refName_eq: $refName }) {
      edges {
        node {
          id
          name
          refName
          columns
        }
      }
    }
  }
`;

const DELETE_COLUMN_SET = gql`
  mutation deleteColumnSet($id: ID!) {
    deleteCustomColumnSet(id: $id) {
      id
      name
      refName
      columns
    }
  }
`;

const UPDATE_COLUMN_SET = gql`
  mutation updateColumnSet($input: CustomColumnSetInput) {
    updateCustomColumnSet(input: $input) {
      id
      name
      columns
    }
  }
`;

export default {
  components: { BasicDialog },
  name: "custom-column-sets",
  props: {
    refName: {
      type: String,
      default: null,
    },
    currentColumns: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      debug: false,
      showDialog: false,
      setName: "",
      selectedSet: "",
    };
  },

  computed: {
    isValid() {
      return this.setName.length > 0 && this.refName !== null;
    },
    saveEnabled() {
      return this.currentColumns.length > 0 && this.refName !== null;
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    setsList() {
      return this.customColumnSets && this.customColumnSets.edges.length > 0
        ? this.customColumnSets.edges.map(removeGQLTypename)
        : [];
    },
  },
  methods: {
    onSaveColumnSet() {
      this.showDialog = true;
      this.setName = this.selectedSet ? this.selectedSet.name : "";
    },
    saveColumnSet() {
      this.showDialog = false;
      const existingSet = this.setsList.find(
        (set) => set.name === this.setName
      );

      this.$apollo
        .mutate({
          mutation: existingSet ? UPDATE_COLUMN_SET : CREATE_COLUMN_SET,
          variables: {
            input: {
              id: existingSet ? existingSet.id : undefined,
              name: this.setName,
              refName: this.refName,
              columns: JSON.stringify(this.currentColumns),
            },
          },
          refetchQueries: [
            {
              query: SETS_LIST,
              variables: { refName: this.refName },
            },
          ],
        })
        .then((result) => {
          //console.log(result);
          this.selectedSet = existingSet
            ? { ...result.data.updateCustomColumnSet }
            : { ...result.data.createCustomColumnSet };
        })
        .catch((error) => {
          const errorMsg = error.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
    onSetChange(payload) {
      if (payload) {
        const set = this.setsList.find((s) => s.id === payload.id);
        if (set) {
          const columns = JSON.parse(set.columns);
          this.$emit("load-columns", columns);
        }
      }
    },
    onDeleteSet(setId) {
      //console.log("onDeleteSet", setId);
      this.$apollo
        .mutate({
          mutation: DELETE_COLUMN_SET,
          variables: { id: setId },
          refetchQueries: [
            { query: SETS_LIST, variables: { refName: this.refName } },
          ],
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Set deleted successfully",
            color: "success",
          });
          this.selectedSet = "";
        })
        .catch((error) => {
          const errorMsg = error.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
  },
  apollo: {
    customColumnSets: {
      query: SETS_LIST,
      variables() {
        return { refName: this.refName };
      },
    },
  },
};
</script>

<style>
</style> 


// WEBPACK FOOTER //
// src/components/gt/shared/CustomColumnSets.vue